'use client';

import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIncidentDetail } from '@/app/hooks/incident/useIncidentDetail';
import { useIncidentStatusTransition } from '@/app/hooks/incident/useIncidentStatusTransition';
import { IncidentService } from '@/app/services/incident/incident.service';
import { isOpenIncidentStatus } from '@/app/dashboard/mock-data';

export function useIncidentDetailScreen(providedIncidentId?: string) {
  const { t } = useTranslation('incidents');
  const navigate = useNavigate();
  const params = useParams<{ incidentId?: string }>();
  const resolvedIncidentId = providedIncidentId ?? params?.incidentId;
  const {
    data: detail,
    error,
    isLoading,
    refetch,
  } = useIncidentDetail(resolvedIncidentId ?? '');

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignee, setAssignee] = useState('');
  const hasPersistedDiagnosis = Boolean(
    detail?.diagnostics.latestDiagnosisSnapshot,
  );
  const {
    blockingReason,
    cancelPendingTransition,
    confirmBlockedTransition,
    isTransitionDialogOpen,
    isTransitioning,
    pendingTransition,
    requestTransition,
    setBlockingReason,
    transitioningIncidentId,
  } = useIncidentStatusTransition();

  const diagnoseIncidentMutation = useMutation({
    mutationFn: async () => {
      if (!resolvedIncidentId?.trim()) {
        throw new Error(
          t('hook.diagnosis.missingIncidentId', 'Incident id is required'),
        );
      }

      return IncidentService.diagnoseIncident(resolvedIncidentId);
    },
    onSuccess: async (result) => {
      // The api answers a diagnostic-engine timeout with an unsaved "No diagnostic available".
      if (result.status === 'NO_DIAGNOSTIC_AVAILABLE') {
        message.warning(
          t(
            'hook.diagnosis.noDiagnosticAvailable',
            'No diagnostic available: the diagnosis took too long. Please try again.',
          ),
        );
        return;
      }

      await refetch();
      message.success(
        t(
          'hook.diagnosis.success',
          'Diagnosis completed and incident details were refreshed.',
        ),
      );
    },
  });

  const triggerRemoteActionMutation = useMutation({
    mutationFn: async () => {
      if (!resolvedIncidentId?.trim()) {
        throw new Error(
          t('hook.remoteActions.missingIncidentId', 'Incident id is required'),
        );
      }

      return IncidentService.triggerRemoteAction(resolvedIncidentId);
    },
    onSuccess: async () => {
      await refetch();
      message.success(
        t(
          'hook.remoteActions.success',
          'Remote actions started and incident details were refreshed.',
        ),
      );
    },
    onError: (mutationError: Error) => {
      message.error(
        mutationError.message ||
          t(
            'hook.remoteActions.failed',
            'Failed to launch remote incident actions.',
          ),
      );
    },
  });

  const launchOnsiteInterventionMutation = useMutation({
    mutationFn: async () => {
      if (!resolvedIncidentId?.trim()) {
        throw new Error(
          t('hook.onsite.missingIncidentId', 'Incident id is required'),
        );
      }

      return IncidentService.launchOnsiteIntervention(resolvedIncidentId);
    },
    onSuccess: () => {
      message.success(
        t('hook.onsite.success', 'Onsite intervention generated successfully.'),
      );
    },
    onError: (mutationError: Error) => {
      message.error(
        mutationError.message ||
          t('hook.onsite.failed', 'Failed to launch onsite intervention.'),
      );
    },
  });

  const handleBack = () => {
    navigate('/incidents');
  };

  const handleOpenAssignModal = () => {
    setAssignModalOpen(true);
  };

  const handleCloseAssignModal = () => {
    setAssignModalOpen(false);
    setAssignee('');
  };

  const handleAssigneeChange = (nextAssignee: string) => {
    setAssignee(nextAssignee);
  };

  const handleConfirmAssign = () => {
    const trimmedAssignee = assignee.trim();

    if (!trimmedAssignee) {
      return;
    }

    setAssignModalOpen(false);
    setAssignee('');
    message.success(
      t('hook.assignedSuccess', {
        assignee: trimmedAssignee,
        defaultValue: 'Incident assigned to {{assignee}}',
      }),
    );
  };

  const handleDiagnose = async () => {
    if (diagnoseIncidentMutation.isPending || hasPersistedDiagnosis) {
      return;
    }

    await diagnoseIncidentMutation.mutateAsync();
  };

  const handleTriggerRemoteAction = async () => {
    if (triggerRemoteActionMutation.isPending) {
      return;
    }
    await triggerRemoteActionMutation.mutateAsync();
  };

  const handleLaunchOnsiteIntervention = async () => {
    if (launchOnsiteInterventionMutation.isPending) {
      return;
    }
    await launchOnsiteInterventionMutation.mutateAsync();
  };

  const handleCloseOnsiteModal = () => {
    launchOnsiteInterventionMutation.reset();
  };

  const handleCopyOnsiteMagicLink = () => {
    const magicLink = launchOnsiteInterventionMutation.data?.magicLink;
    if (magicLink) {
      navigator.clipboard.writeText(magicLink);
      message.success(
        t('hook.successModal.copied', 'Link copied to clipboard'),
      );
    }
  };

  return {
    assignModalOpen,
    assignee,
    blockingReason,
    canDispatch: detail ? isOpenIncidentStatus(detail.incident.status) : false,
    confirmBlockedTransition,
    cancelPendingTransition,
    detail,
    errorMessage: error?.message ?? null,
    hasPersistedDiagnosis,
    handleAssigneeChange,
    handleBack,
    handleCloseAssignModal,
    handleCloseOnsiteModal,
    handleConfirmAssign,
    handleCopyOnsiteMagicLink,
    handleDiagnose,
    handleLaunchOnsiteIntervention,
    handleOpenAssignModal,
    handleTriggerRemoteAction,
    handleStatusTransition: requestTransition,
    isDiagnosing: diagnoseIncidentMutation.isPending,
    isLaunchingRemoteAction: triggerRemoteActionMutation.isPending,
    isLaunchingOnsiteIntervention: launchOnsiteInterventionMutation.isPending,
    isLoading,
    isTransitionDialogOpen,
    isTransitioning,
    onsiteMagicLink: launchOnsiteInterventionMutation.data?.magicLink ?? null,
    onsiteRecommendedActions:
      launchOnsiteInterventionMutation.data?.recommendedActions ?? [],
    pendingTransition,
    setBlockingReason,
    showAssignAction: detail?.incident.status === 'FIELD_REQUIRED',
    transitioningIncidentId,
  };
}
